<template>
    <div>
        <card-header title="Import SABRE" icon="fa-file-import"/>

        <card-body>
            <card-list v-if="!legs.length">
                <container-list-item padded>
                    <div class="mb-3 is-size-7">Upload a SABRE itinerary PDF and each flight will be read into a form for review before anything is saved.</div>
                    <file-pond
                        name="file"
                        ref="pond"
                        label-idle="Drop or click to add a SABRE itinerary PDF..."
                        :allow-multiple="false"
                        :allowRevert="false"
                        accepted-file-types="application/pdf"
                        :files="files"
                        @processfile="handleFilePondProcess"
                        :server="{
                                url: apiUrl,
                                timeout: 15000,
                                process: {
                                    url: 'api/registrations/' + props.registrationId + '/flights/import',
                                    method: 'POST',
                                    headers: {
                                        'Authorization': token
                                    },
                                    withCredentials: false
                                }
                            }"
                    />
                    <div class="notification is-danger is-light" v-if="error">{{error}}</div>
                </container-list-item>
            </card-list>

            <div v-else>
                <card-list>
                    <container-list-item>
                        <data-row title="Reservation">{{reservationCode || '—'}}</data-row>
                        <data-row title="Travelers">
                            <div v-for="t in travelers" :key="t.name">
                                {{t.name}}
                                <span v-if="t.match === 'primary'" class="is-size-7 has-text-grey">(Primary)</span>
                                <span v-else-if="t.match === 'secondary'" class="is-size-7 has-text-grey">(Secondary)</span>
                                <span v-else class="has-text-danger is-size-7"><i class="fas fa-exclamation-triangle mr-1"/>No match on this registration</span>
                            </div>
                        </data-row>
                    </container-list-item>
                </card-list>

                <div v-for="(leg, index) in legs" :key="index">
                    <card-list>
                        <subheader-list-item icon="fa-plane" :title="legTitle(leg, index)"/>
                        <container-list-item padded>
                            <b-switch :true-value="true" :false-value="false" v-model="leg.included">Import this flight</b-switch>
                            <div class="mt-2" v-if="leg.warnings.length">
                                <div class="has-text-danger is-size-7" v-for="w in leg.warnings" :key="w"><i class="fas fa-exclamation-triangle mr-2"/>{{w}}</div>
                            </div>
                        </container-list-item>
                    </card-list>
                    <flight-form v-if="leg.included" :flight="leg" :registration="registration" :itinerary="itinerary"/>
                </div>
            </div>
        </card-body>

        <card-footer class="pt-3 pb-3 has-text-right" v-if="legs.length">
            <b-button size="is-small" class="mr-2" @click="reset"><i class="fas fa-undo mr-2"></i>Start Over</b-button>
            <b-button type="is-primary" size="is-small" :disabled="!canSubmit" @click="submit"><i class="fas fa-plus mr-3"></i>Import {{includedLegs.length}} Flight{{includedLegs.length === 1 ? '' : 's'}}</b-button>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from '../../TIER/components/CardHeader';
    import CardBody from '../../TIER/components/CardBody';
    import CardFooter from '../../TIER/components/CardFooter';
    import CardList from '../../TIER/components/CardList';
    import ContainerListItem from '../../TIER/components/ContainerListItem';
    import SubheaderListItem from '../../TIER/components/SubheaderListItem';
    import DataRow from '../../TIER/components/DataRow';
    import FlightForm from './FlightForm';

    import {client as http} from '../../http_client';
    import {mapActions, mapGetters} from 'vuex';

    import vueFilePond from 'vue-filepond';
    import 'filepond/dist/filepond.min.css';
    import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';

    const FilePond = vueFilePond(FilePondPluginFileValidateType);

    export default {
        props: ['card', 'props'],
        components: {DataRow, SubheaderListItem, ContainerListItem, CardList, CardFooter, CardBody, CardHeader, FlightForm, FilePond},
        data: function () {
            return {
                apiUrl: '/',
                registration: {
                    user: {}
                },
                itinerary: [],
                files: [],
                error: null,
                reservationCode: null,
                travelers: [],
                legs: []
            };
        },
        computed: {
            ...mapGetters(['token']),
            includedLegs() {
                return this.legs.filter(l => l.included);
            },
            canSubmit() {
                return this.includedLegs.length > 0 && this.includedLegs.every(l =>
                    l.arranged_by && l.depart_airport && l.arrive_airport && l.depart_date && l.depart_time && l.arrive_date && l.arrive_time && l.flight_number);
            }
        },
        methods: {
            ...mapActions(['updateTasks']),
            loadRegistration: function() {
                this.$emit('loading', true);
                http.get('/api/registrations/' + this.props.registrationId, {force: true}).then(response => {
                    this.registration = response.data;

                    http.get('/api/registrations/' + this.props.registrationId + '/itinerary', {force: true}).then(response => {
                        this.itinerary = response.data.days;
                        this.$emit('loading', false);
                    });
                });
            },
            legTitle(leg, index) {
                const from = leg.depart_airport ? leg.depart_airport.iata : '?';
                const to = leg.arrive_airport ? leg.arrive_airport.iata : '?';
                return 'Flight ' + (index + 1) + ': ' + (leg.flight_number || 'Unknown') + ' — ' + from + ' to ' + to;
            },
            handleFilePondProcess: function(err, response) {
                if (err) {
                    this.error = 'Could not read any flights from that PDF. Is it a SABRE itinerary?';
                    return;
                }
                const result = JSON.parse(response.serverId);
                this.error = null;
                this.reservationCode = result.reservation_code;
                this.travelers = result.travelers;
                // Pre-seed every field FlightForm binds to so Vue 2 reactivity works.
                this.legs = result.legs.map(leg => Object.assign({
                    day_id: 0,
                    second_day_id: 0,
                    has_arrived: 0,
                    primary_flight: 0,
                    seat_preference: null,
                    cost: null,
                    included: true
                }, leg));
            },
            reset() {
                this.legs = [];
                this.travelers = [];
                this.reservationCode = null;
                this.error = null;
            },
            submit() {
                this.$emit('loading', true);
                const posts = this.includedLegs.reduce((chain, leg) =>
                    chain.then(() => http.post('/api/registrations/' + this.props.registrationId + '/flights', leg, {force: true})),
                    Promise.resolve());
                posts.then(() => {
                    this.updateTasks();
                    this.$reloadCard('registrations');
                    this.$reloadCard('registration', {registrationId: this.props.registrationId});
                    this.$reloadCard('flights', {registrationId: this.props.registrationId});
                    this.$reloadCard('tasks');
                    this.$reloadCard('task-details', {type: 'flights'});
                    this.$saveAlert(false);
                    this.$closeCard(this.card);
                }).catch(err => {
                    this.$emit('loading', false);
                });
            }
        },
        mounted() {
            this.loadRegistration();
            this.$saveAlert(true, 'Are you done importing these flights? Unsaved changes will be lost.');
        }
    };
</script>

<style>
    .filepond--credits {
        display: none !important;
    }
</style>
